import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { createPortal } from 'react-dom';
import { Children, cloneElement, forwardRef, isValidElement, useEffect, useRef, useState } from 'react';
import { FOCUSABLE_ELEMENTS, KEY_CODES, KEY_DOWN } from '@outdoor-voices/ui/dist/config/constants';
import { lockScroll, unlockScroll } from '@outdoor-voices/ui/dist/utils/dom/scroll';
import { XLarge } from '@outdoor-voices/ui';
import cc from 'classcat';
import PropTypes from 'prop-types';
import css from './index.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var dialogDivId = 'ov-dialogs';

var dialogDiv = function dialogDiv() {
  var div = document.querySelector("#".concat(dialogDivId));

  if (div) {
    return div;
  }

  div = document.createElement('div');
  div.id = dialogDivId;
  document.body.appendChild(div);
  return div;
};

var Overlay = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref2;

  var ariaDescribedBy = _ref.ariaDescribedBy,
      ariaLabelledBy = _ref.ariaLabelledBy,
      backgroundButton = _ref.backgroundButton,
      children = _ref.children,
      label = _ref.label,
      medium = _ref.medium,
      noPadding = _ref.noPadding,
      onClose = _ref.onClose,
      open = _ref.open,
      small = _ref.small,
      wide = _ref.wide;
  var describedBy = ariaDescribedBy ? {
    'aria-describedby': ariaDescribedBy
  } : null;
  var labelledBy = ariaLabelledBy ? {
    'aria-labelledby': ariaLabelledBy
  } : null;

  var accessibilities = _objectSpread(_objectSpread({
    'aria-label': "".concat(label, " dialog"),
    'aria-modal': 'true'
  }, describedBy), labelledBy);

  var childrenWithProps = Children.map(children, function (child) {
    // Checking isValidElement is the safe way and avoids a
    // typescript error too.
    if ( /*#__PURE__*/isValidElement(child)) {
      return /*#__PURE__*/cloneElement(child, {
        onClose: onClose
      });
    }

    return child;
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({}, accessibilities), {}, {
      className: cc([css.root, (_ref2 = {}, _defineProperty(_ref2, css.on, open), _defineProperty(_ref2, css.noPadding, noPadding), _defineProperty(_ref2, css.medium, medium), _defineProperty(_ref2, css.small, small), _defineProperty(_ref2, css.wide, wide), _ref2)]),
      ref: ref,
      role: "dialog",
      children: childrenWithProps
    })), backgroundButton]
  });
});
Overlay.propTypes = {
  ariaDescribedBy: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  backgroundButton: PropTypes.node,
  children: PropTypes.node,
  label: PropTypes.string,
  medium: PropTypes.bool,
  noPadding: PropTypes.bool,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  small: PropTypes.bool,
  wide: PropTypes.bool
};

var Ovly = function Ovly(_ref3) {
  var ariaDescribedBy = _ref3.ariaDescribedBy,
      ariaLabelledBy = _ref3.ariaLabelledBy,
      button = _ref3.button,
      children = _ref3.children,
      label = _ref3.label,
      _ref3$noPadding = _ref3.noPadding,
      noPadding = _ref3$noPadding === void 0 ? false : _ref3$noPadding,
      _ref3$onHide = _ref3.onHide,
      onHide = _ref3$onHide === void 0 ? function () {} : _ref3$onHide,
      _ref3$onBeforeShow = _ref3.onBeforeShow,
      onBeforeShow = _ref3$onBeforeShow === void 0 ? function () {} : _ref3$onBeforeShow,
      _ref3$onShow = _ref3.onShow,
      onShow = _ref3$onShow === void 0 ? function () {} : _ref3$onShow,
      _ref3$small = _ref3.small,
      small = _ref3$small === void 0 ? false : _ref3$small,
      _ref3$medium = _ref3.medium,
      medium = _ref3$medium === void 0 ? false : _ref3$medium,
      _ref3$wide = _ref3.wide,
      wide = _ref3$wide === void 0 ? false : _ref3$wide;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      closing = _useState2[0],
      setClosing = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      locked = _useState4[0],
      setLocked = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      on = _useState6[0],
      setOn = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      open = _useState8[0],
      setOpen = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      opening = _useState10[0],
      setOpening = _useState10[1];

  var buttonRef = useRef(null);
  var dialogRef = useRef(null);
  useEffect(function () {
    var off = function off() {
      setClosing(false);
      setOn(false);
      setOpen(false);
      setOpening(false);
    };

    var close = function close() {
      setClosing(true);
      setOpen(false);
      setTimeout(off, 200);
    };

    var onOn = function onOn() {
      if (!on) {
        return;
      }

      setOpening(false);
      setOpen(true);
    };

    if (on && !open && !opening && !closing) {
      setOpening(true);
      setTimeout(onOn, 100);
    }

    if (on && closing && !open) {
      close();
    }
  }, [closing, on, open, opening]);
  useEffect(function () {
    var dialog = dialogRef.current;
    var openButton = buttonRef.current;

    if (!dialog || !on) {
      return;
    }

    var onKeyDown = function onKeyDown(event) {
      // add all the elements which you want to make focusable
      var focusableContent = dialog.querySelectorAll(FOCUSABLE_ELEMENTS);
      var first = focusableContent[0];
      var last = focusableContent[focusableContent.length - 1];
      var isTabPressed = event.key === KEY_CODES.TAB;

      if (event.key === KEY_CODES.ESC) {
        setOpen(false);
        setClosing(true);

        if (onHide) {
          onHide(event);
        }

        return;
      }

      if (!isTabPressed) {
        return;
      }

      var activeElementIndex = _toConsumableArray(focusableContent).indexOf(document.activeElement);

      if (event.shiftKey) {
        // if shift key pressed for shift + tab combination
        if (document.activeElement === first || activeElementIndex < 0) {
          last.focus(); // add focus for the last focusable element

          event.preventDefault();
        }
      } else {
        // if tab key is pressed
        // eslint-disable-next-line no-lonely-if
        if (document.activeElement === last || activeElementIndex < 0) {
          // if focused has reached to last focusable element then focus first focusable element after pressing tab
          first.focus();
          event.preventDefault();
        }
      }
    };

    if (!on || !open) {
      document.removeEventListener(KEY_DOWN, onKeyDown);
      return;
    }

    document.addEventListener(KEY_DOWN, onKeyDown);
    var timeout = {};

    if (dialog) {
      var focusableContent = dialog.querySelectorAll(FOCUSABLE_ELEMENTS);
      var first = focusableContent[0];
      timeout = setTimeout(function () {
        first.focus();

        if (onShow) {
          onShow();
        }
      }, 100);
    }

    return function () {
      clearTimeout(timeout);
      document.removeEventListener(KEY_DOWN, onKeyDown); // Refocus the element on the original focused element

      if (openButton) {
        openButton.focus();
      }
    };
  }, [open, on, onHide, onShow]); // Handle scroll locking

  useEffect(function () {
    if (on && locked) {
      return;
    }

    if (on && !locked) {
      setLocked(true);
      lockScroll();
      return;
    }

    if (!locked) {
      return;
    }

    unlockScroll();
    setLocked(false);
  }, [locked, on]);

  var onClose = function onClose() {
    setOpen(false);
    setClosing(true);
  };

  var closeARIA = "Close the ".concat(label, " dialog");

  var backgroundButton = /*#__PURE__*/_jsx("button", {
    "aria-label": closeARIA,
    className: cc([css.bg, _defineProperty({}, css.on, on)]),
    onClick: onClose,
    type: "button"
  });

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("button", {
      "aria-label": "Open the ".concat(label, " dialog"),
      className: cc([css.button, _defineProperty({}, css.on, on)]),
      onClick: function onClick() {
        onBeforeShow();
        setOn(true);
        setOpen(false);
        setOpening(false);
        setClosing(false);
      },
      ref: buttonRef,
      type: "button",
      children: button
    }), on && /*#__PURE__*/createPortal( /*#__PURE__*/_jsxs(Overlay, {
      ariaDescribedBy: ariaDescribedBy,
      ariaLabelledBy: ariaLabelledBy,
      backgroundButton: backgroundButton,
      label: label,
      open: open,
      onClose: onClose,
      medium: medium,
      noPadding: noPadding,
      ref: dialogRef,
      small: small,
      wide: wide,
      children: [children, /*#__PURE__*/_jsx("button", {
        "aria-label": closeARIA,
        className: css.close,
        onClick: onClose,
        type: "button",
        children: /*#__PURE__*/_jsx(XLarge, {})
      })]
    }), dialogDiv())]
  });
};

Ovly.propTypes = {
  ariaDescribedBy: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  button: PropTypes.node,
  children: PropTypes.node,
  label: PropTypes.string,
  noPadding: PropTypes.bool,
  medium: PropTypes.bool,
  onHide: PropTypes.func,
  onBeforeShow: PropTypes.func,
  onShow: PropTypes.func,
  small: PropTypes.bool,
  wide: PropTypes.bool
};
export default Ovly;